<template>
  <div v-if="isLoading" class="animate-pulse bg-gray-300/50 dark:bg-gray-300 w-full h-[240px] rounded-[0.5rem]" />
  <section
    v-else
    :class="`p-2 px-3 md:p-4 rounded-[0_0_11px_11px] md:rounded-[11px] bg-white border border-[#f3f3f3]`">
    <!-- Verified Purchases Label -->
    <div 
      v-if="firstComments.length > 0"
      class="flex items-center gap-1.5 mb-3"
    >
      <svg class="w-[14px] h-[14px] md:w-[16px] md:h-[16px] text-[#22c55e]" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
      </svg>
      <span class="text-[12px] md:text-[13px] lg:text-[14px] text-[#22c55e] font-medium">
        {{ $t('user.all_ratings_verified_purchases') }}
      </span>
    </div>
    <!-- card head -->
    <div class="flex justify-between items-center pt-1 pb-1.5 border-[#E5E5E5] border-b-[1px]">
      <div v-if="firstComments.length > 0" class="flex flex-nowrap gap-2">
        <img src="@/assets/icons/star.svg" class="w-[1.513rem] h-[1.513rem] md:w-[1.813rem] md:h-[1.813rem]" alt />
        <div>
          <!-- section title -->
          <StoreSectionTitle :title="$t('user.store_ratings')" />
          <!-- voters count -->
          <div v-if="typeof storeItem.ratings_count === 'number'" class="text_size15 text-[#8E8E8E] mt-1">
            {{ $t('user.store.viewers_count', {
      count: arabizeNumbers(Number(storeItem.ratings_count).toLocaleString())
    }) }}
          </div>
        </div>
      </div>
      <div v-else>
        <span class="text-[1.188rem] md:text-[1.24rem] 3xl:text-[1.313rem] text-gray-500 font-bold text-center">
          {{ $t('user.store.no_ratings_for_now') }}
        </span>
      </div>
      <button v-if="!hasAlreadyCommented" @click="() => handleViewingStoreInteractionModal('rate')"
        class="flex flex-nowrap !outline-none text-primary justify-center items-center group py-4 gap-x-2.5 hover:text-primaryLighter transition-colors duration-150 ease-linear">
        <span class="text_size16">{{ $t('user.rate_store') }}</span>
        <CaretLeftIcon
          class="group-hover:translate-x-[-2px] transition-[transform] duration-150 w-[0.5rem] md:w-[0.6rem] h-auto ltr:rotate-180" />
      </button>
    </div>
    <!-- card body -->
    <div class="w-full">
      <Feedbacks :typeRate="typeRate" :isDeletingRate="isDeletingRate"
      :isReportingRate="isReportingRate" :hasReachedFullPages="true" :isLoading="isLoading" :items="firstComments" :disableDeletingAndReporting="false" :handleLikeClick="handleLikeClick" :confirmDeletingRate="confirmDeletingRate" :confirmReportingRate="confirmReportingRate" :onEditRatingOpenModal="onEditRatingOpenModal" />
    </div>
    <!-- card footer -->
    <div v-if="firstComments && firstComments.length > 0" class="border-[#E5E5E5] border-t-[1px]">
      <button @click="() => handleViewingStoreInteractionModal('comments')"
        class="flex justify-center !outline-none items-center w-full group pt-4 gap-1 hover:bg-[#00000002] transition-colors duration-150 ease-linear">
        <span class="text_size16 text-primary">{{ $t('user.store.see_more_ratings') }}</span>
        <CaretLeftIcon
          class="group-hover:translate-x-[-2px] transition-[transform] duration-150 w-[0.5rem] md:w-[0.6rem] h-auto ltr:rotate-180" />
      </button>
    </div>
  </section>
</template>

<script>
import CaretLeftIcon from "@/assets/shared/icons/CaretLeftIcon.vue";
import StoreSectionTitle from "./StoreSectionTitle.vue";
import Feedbacks from "@/components/user/feedbacks/Feedbacks.vue";
import { arabizeNumbers } from "@/helpers";
import { useGlobalStore } from '@/store/Modules/global';

export default {
  setup(){
        const global = useGlobalStore() || {};
        return {
            global,
            isDeletingRate: false,
            isReportingRate: false,
        }
  },
  props: {
    storeItem: {
      type: Object,
      required: true,
      default: () => ({})
    },
    isLoading: {
      type: Boolean,
      required: true,
      default: false,
    },
    firstComments: {
      type: Array,
      required: true,
      default: () => ([])
    },
    handleViewingStoreInteractionModal: {
      type: Function,
      required: true
    },
    handleLikeClick: {
      type: Function,
      required: false
    },
    onEditRatingOpenModal: {
      type: Function,
      required: false
    },
  },
  computed:{
    hasAlreadyCommented(){
      const currentUserId = this.global?.localStorageCopy?.user?.id;
      return this.firstComments?.some((item) => item.user_id == currentUserId);
    },
    typeRate(){
      return 'stores';
    }
  },
  methods: {
    arabizeNumbers,
    confirmReportingRate({rateForm, item}){
            this.isReportingRate = true;
            const ratingId = item?.id;
            // adjust the endpoint and formData
            if (!ratingId) {
                return;
            }
            const bodyRequest = {
                rating_id: ratingId,
                comment: rateForm.comment
            }

            this.makeRequest({
                owner: this.MAINROLES.user,
                url: this.APIREQUESTS.user.reportStoreRating.url,
                method: this.APIREQUESTS.user.reportStoreRating.method,
                formData: JSON.stringify(bodyRequest)
            })
                .then(res => {
                if (res?.status) {
                    this.notify({
                    type: "success",
                    msg: res?.data?.message || this.$t("general.succeeded")
                    });
                    this.$router.go(0);
                } else {
                    this.notify({
                    type: "error",
                    msg: res?.message || this.$t("general.failed")
                    });
                }
                })
                .catch(err => {
                this.notify({
                    type: "error",
                    msg: err?.data?.message || this.$t("general.failed")
                    });
                })
                .finally(() => {
                this.isReportingRate = false;
                });
        },
        confirmDeletingRate(ratingId) {
            this.isDeletingRate = true;
            if (!ratingId) {
                return;
            }
            this.makeRequest({
                owner: this.MAINROLES.user,
                url: `${this.APIREQUESTS.user.deleteStoreRating.url}/${ratingId}`,
                method: this.APIREQUESTS.user.deleteStoreRating.method,
            })
                .then((res) => {
                    if (res?.status) {
                      this.$router.go(0);
                    } else {
                        this.notify({
                            type: "error",
                            msg: res?.message || this.$t("general.failed"),
                        });
                    }
                })
                .catch((err) => {
                    this.notify({
                        type: "error",
                        msg: err?.data?.message || this.$t("general.failed"),
                    });
                })
                .finally(() => {
                    this.isDeletingRate = false;
                });
        },
  },
  components: {
    StoreSectionTitle,
    CaretLeftIcon,
    Feedbacks,

  }
}
</script>

<style></style>